import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ScrollAnimation from '../components/ScrollAnimation';
import { fadeInUp, fadeInLeft, staggerContainer, popIn } from '../utils/animations';
import { getImage } from '../utils/imageImports';
import './css/thingstodo.css';

const attractions = [
  {
    id: 1,
    title: 'Ghost Tours',
    description: "Explore the haunted streets of Edinburgh with expert guides and discover the city's dark past.",
    image: getImage('ghost_tours.jpg'),
    link: '/info',
    category: 'tours'
  },
  {
    id: 2,
    title: 'Edinburgh Castle',
    description: "Visit the iconic castle and uncover its dark history. Explore the ancient fortress atop Castle Rock.",
    image: getImage('edinburgh_castle.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 3,
    title: 'Underground Vaults',
    description: "Discover the hidden tunnels beneath the city and learn about Edinburgh's mysterious underground world.",
    image: getImage('edinburgh_valuts.jpg'),
    link: '/info',
    category: 'tours'
  },
  {
    id: 4,
    title: "Arthur's Seat",
    description: "Hike up the ancient volcano for breathtaking views of the city and surrounding landscapes.",
    image: getImage('arthurs_seat.jpg'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 5,
    title: 'Royal Mile',
    description: "Walk down the historic Royal Mile, connecting Edinburgh Castle with the Palace of Holyroodhouse.",
    image: getImage('royal_mile.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 6,
    title: 'National Museum',
    description: "Explore Scotland's history, culture, and people through interactive exhibits and treasured artifacts.",
    image: getImage('national_museum.jpg'),
    link: '/info',
    category: 'museums'
  },
  {
    id: 7,
    title: 'Calton Hill',
    description: "Visit this UNESCO World Heritage Site featuring iconic monuments and panoramic city views.",
    image: getImage('calton_hill.jpg'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 8,
    title: 'Princess Street Gardens',
    description: "Relax in these beautiful public gardens located in the heart of Edinburgh's city center.",
    image: getImage('princess_street_gardens.jpg'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 9,
    title: 'Palace of Holyroodhouse',
    description: "Tour the Queen's official residence in Scotland and explore its historic chambers and gardens.",
    image: getImage('palace_of_holyroodhouse.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 10,
    title: 'Scotch Whisky Experience',
    description: "Learn about Scotland's national drink with guided tours and tastings of fine Scotch whiskies.",
    image: getImage('scotch_whisky_experience.jpg'),
    link: '/info',
    category: 'entertainment'
  },
  {
    id: 11,
    title: 'Greyfriars Bobby',
    description: "Visit the statue and grave of Edinburgh's most loyal dog and hear his heartwarming story.",
    image: getImage('greyfriars_bobby.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 12,
    title: 'Rosslyn Chapel',
    description: "Discover the intricate carvings and mysterious symbolism of this 15th-century chapel.",
    image: getImage('rosslyn_chapel.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 13,
    title: "Real Mary King's Close",
    description: "Step beneath the Royal Mile to discover a hidden world of 17th-century streets frozen in time.",
    image: getImage('real_mary_kings_close.jpg'),
    link: '/info',
    category: 'tours'
  },
  {
    id: 14,
    title: 'Camera Obscura',
    description: "Experience optical illusions and interactive exhibits at this fascinating attraction on the Royal Mile.",
    image: getImage('camera_obscura.jpg'),
    link: '/info',
    category: 'entertainment'
  },
  {
    id: 15,
    title: 'Dean Village',
    description: "Explore this picturesque village on the Water of Leith with its beautiful architecture and tranquil atmosphere.",
    image: getImage('dean_village.jpg'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 16,
    title: 'Edinburgh Dungeon',
    description: "Experience the darker side of Edinburgh's history through actor-led shows and special effects.",
    image: getImage('edinburgh_dungeons.jpg'),
    link: '/info',
    category: 'entertainment'
  },
  {
    id: 17,
    title: "Surgeons' Hall Museum",
    description: "Discover one of the oldest museums in Scotland with its fascinating collection of surgical specimens and artifacts.",
    image: getImage('surgeons_hall.jpg'),
    link: '/info',
    category: 'museums'
  },
  {
    id: 18,
    title: 'Scottish Parliament',
    description: "Tour the award-winning contemporary building that houses Scotland's devolved parliament.",
    image: getImage('scottish_parliament.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 19,
    title: 'Scottish National Gallery',
    description: "View masterpieces by Raphael, Velázquez, Monet, and many other renowned artists.",
    image: getImage('scottish_national_gallery.jpg'),
    link: '/info',
    category: 'museums'
  },
  {
    id: 20,
    title: 'Craigmillar Castle',
    description: "Explore one of Scotland's most perfectly preserved medieval castles, once a refuge for Mary, Queen of Scots.",
    image: getImage('craigmillar_castle.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 21,
    title: 'Grassmarket',
    description: "Visit this historic market place with its vibrant atmosphere, restaurants, and views of Edinburgh Castle.",
    image: getImage('grassmarket.jpg'),
    link: '/info',
    category: 'entertainment'
  },
  {
    id: 22,
    title: 'Leith',
    description: "Explore Edinburgh's port district with its historic maritime heritage and modern dining scene.",
    image: getImage('leith.jpg'),
    link: '/info',
    category: 'entertainment'
  },
  {
    id: 23,
    title: 'Edinburgh Zoo',
    description: "Meet over 1,000 animals including the UK's only giant pandas at this world-class conservation zoo.",
    image: getImage('edinburgh_zoo.jpg'),
    link: '/info',
    category: 'entertainment'
  },
  {
    id: 24,
    title: 'The Meadows',
    description: "Relax in this large public park south of the Old Town, popular for sports and sunny day picnics.",
    image: getImage('the_meadows.jpg'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 25,
    title: 'Water of Leith Walkway',
    description: "Follow this scenic footpath along the river through the heart of Edinburgh's most charming neighborhoods.",
    image: getImage('water_of_leith.jpg'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 26,
    title: 'Royal Botanic Garden',
    description: "Wander through 70 acres of beautiful gardens featuring over 13,000 plant species from around the world.",
    image: getImage('royal_botanic_garden.JPG'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 27,
    title: "The Writers' Museum",
    description: "Discover the lives of Scotland's literary giants: Robert Burns, Sir Walter Scott, and Robert Louis Stevenson.",
    image: getImage('the_writers_museum.jpg'),
    link: '/info',
    category: 'museums'
  },
  {
    id: 28,
    title: "St Giles' Cathedral",
    description: "Visit this historic church on the Royal Mile with its distinctive crown spire and beautiful stained glass.",
    image: getImage('st_giles_cathedral.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 29,
    title: 'Stockbridge',
    description: "Explore this charming village-like neighborhood known for its independent shops and Sunday market.",
    image: getImage('stockbridge.jpg'),
    link: '/info',
    category: 'entertainment'
  },
  {
    id: 30,
    title: 'Lauriston Castle',
    description: "Discover this 16th-century tower house with its beautiful gardens and panoramic views of the Firth of Forth.",
    image: getImage('lauriston_castle.jpg'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 31,
    title: 'Portobello Beach',
    description: "Enjoy Edinburgh's seaside resort with its sandy beach, promenade, and traditional Victorian architecture.",
    image: getImage('visit_scotland.jpg'),
    link: '/info',
    category: 'nature'
  },
  {
    id: 32,
    title: 'Dynamic Earth',
    description: "Take a journey through time at this interactive museum exploring the planet's past, present, and future.",
    image: getImage('dynamic_earth.png'),
    link: '/info',
    category: 'museums'
  },
  {
    id: 33,
    title: 'Blackness Castle',
    description: "Visit this 15th-century fortress known as 'the ship that never sailed' due to its ship-like shape.",
    image: getImage('blackness_castle.png'),
    link: '/info',
    category: 'historic'
  },
  {
    id: 34,
    title: 'Jupiter Artland',
    description: "Explore this contemporary sculpture park and art gallery set in 100 acres of woodland.",
    image: getImage('jupiter_artland.jpg'),
    link: '/info',
    category: 'museums'
  },
  {
    id: 35,
    title: "The Witches' Well",
    description: "Discover this memorial to the hundreds of women executed as witches in Edinburgh between 15th-18th centuries.",
    image: getImage('witches_well.jpg'),
    link: '/info',
    category: 'historic'
  }
];

const ThingsToDo = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(12);
  const [hasMore, setHasMore] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to handle adding an attraction to calendar
  const handleAddToCalendar = (attraction) => {
    // Extract the raw image path from the attraction
    // This ensures the path is stored, not the processed URL
    const rawImagePath = attraction.image.split('/src/assets/')[1]?.split('.')[0] || 
                         attraction.image.split('/assets/')[1]?.split('.')[0] || 
                         attraction.image;
    
    // Store the selected attraction in sessionStorage with raw image path
    sessionStorage.setItem('selectedAttraction', JSON.stringify({
      id: attraction.id,
      title: attraction.title,
      image: rawImagePath // Store only the path without extension
    }));
    
    // Navigate to the calendar page
    navigate('/calendar');
  };

  const cardsPerRow = () => {
    if (window.innerWidth >= 1200) return 4;
    if (window.innerWidth >= 992) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  };

  // Parse URL parameters to check for attraction to scroll to
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const attractionId = searchParams.get('attraction');
    
    // Load the attractions data with a loading delay for animation
    const timer = setTimeout(() => {
      setIsLoaded(true);
      setHasMore(attractions.length > visibleCount);
      
      // If there's an attraction ID in the URL, scroll to it after data loads
      if (attractionId) {
        // Small delay to ensure attraction cards are rendered
        setTimeout(() => {
          scrollToAttraction(attractionId);
        }, 300);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [location.search]);

  // Update hasMore state when visibleCount changes
  useEffect(() => {
    setHasMore(attractions.length > visibleCount);
  }, [visibleCount]);

  // Update cardsPerRow when window size changes
  useEffect(() => {
    const handleResize = () => {
      // Ensure load more button shows appropriate number of cards per screen size
      const rowsToAdd = 2;
      const currentVisible = visibleCount;
      const totalVisible = currentVisible + (cardsPerRow() * rowsToAdd);
      
      if (attractions.length <= currentVisible) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [visibleCount]);

  // Function to scroll to a specific attraction card
  const scrollToAttraction = (targetId) => {
    setIsScrolling(true);
    
    const targetAttraction = attractions.find(attraction => 
      attraction.id?.toString() === targetId?.toString() || 
      attraction.title?.toLowerCase().replace(/\s+/g, '-') === targetId
    );
    
    if (targetAttraction) {
      // Find the index of the target attraction
      const targetIndex = attractions.findIndex(a => a.id === targetAttraction.id);
      
      // Ensure we show enough cards to display the target
      if (targetIndex >= visibleCount) {
        // Calculate how many rows we need to display
        const rowsNeeded = Math.floor(targetIndex / cardsPerRow()) + 1;
        const cardsNeeded = rowsNeeded * cardsPerRow();
        setVisibleCount(cardsNeeded);
      }
      
      // Delay scroll to allow state update and render
      setTimeout(() => {
        const element = document.getElementById(`attraction-${targetAttraction.id}`);
        if (element) {
          // Scroll to element with smooth animation
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Highlight the card
          element.classList.add('highlight-card');
          
          // Remove highlight after animation
          setTimeout(() => {
            element.classList.remove('highlight-card');
            setIsScrolling(false);
          }, 3000);
        } else {
          setIsScrolling(false);
        }
      }, 300);
    } else {
      setIsScrolling(false);
    }
  };

  // Filter attractions based on category and search query
  const filteredAttractions = attractions.filter(attraction => {
    const matchesSearch = 
      attraction.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      attraction.description.toLowerCase().includes(searchQuery.toLowerCase());
    return (selectedCategory === 'all' || attraction.category === selectedCategory) && matchesSearch;
  });

  // Handle loading more attractions
  const loadMore = () => {
    const rowsToAdd = 2;
    const newVisibleCount = visibleCount + (cardsPerRow() * rowsToAdd);
    setVisibleCount(newVisibleCount);
    
    if (newVisibleCount >= filteredAttractions.length) {
      setHasMore(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 50,
      rotateX: 5,
      scale: 0.9
    },
    visible: index => ({ 
      opacity: 1,
      y: 0,
      rotateX: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        delay: Math.min(index * 0.05, 0.8) // Cap the delay to prevent too long delays
      }
    }),
    hover: {
      y: -10,
      boxShadow: "0px 20px 25px rgba(0, 0, 0, 0.5), 0px 15px 15px rgba(212, 175, 55, 0.2)",
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 15 
      }
    },
    exit: {
      opacity: 0,
      y: 20,
      transition: { duration: 0.2 }
    }
  };

  // Page transition animations
  const pageVariants = {
    initial: { opacity: 0 },
    enter: { 
      opacity: 1,
      transition: { 
        duration: 0.7,
        when: "beforeChildren" 
      }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.5 } 
    }
  };

  return (
    <motion.div 
      className="thingstodo-page"
      initial="initial"
      animate="enter"
      exit="exit"
      variants={pageVariants}
      ref={contentRef}
    >
      <ScrollAnimation variants={fadeInUp}>
        <div className="thingstodo-header">
          <h1>Things To Do in Edinburgh</h1>
          <p className="gothic-tagline">Discover the mysteries of Scotland's ancient capital</p>
          
          <div className="search-container">
            <input
              type="text"
              placeholder="Search attractions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <motion.div 
              className="search-icon"
              whileTap={{ scale: 0.9 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </motion.div>
          </div>
        </div>
      </ScrollAnimation>

      {/* Enhanced suggestions section */}
      <ScrollAnimation variants={fadeInLeft} className="suggestions-section">
        <h2>Our Suggestions</h2>
        <ul className="suggestion-list">
          {attractions.slice(0, 5).map((suggestion, index) => (
            <motion.li 
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
              whileHover={{ 
                x: 10, 
                color: "#d4af37", 
                transition: { duration: 0.2 } 
              }}
              onClick={() => !isScrolling && scrollToAttraction(suggestion.id)}
            >
              <span className="suggestion-number">{index + 1}</span>
              <span className="suggestion-text">{suggestion.title}</span>
            </motion.li>
          ))}
        </ul>
      </ScrollAnimation>

      {/* Attractions Cards */}
      <section className="attractions-section">
        <ScrollAnimation variants={fadeInUp}>
          <h2>All Attractions</h2>
        </ScrollAnimation>
        
        <AnimatePresence>
          {!isLoaded ? (
            <div className="loading-container">
              <motion.div 
                className="loading-spinner"
                animate={{ rotate: 360 }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 1.5, 
                  ease: "linear" 
                }}
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="loading-text"
              >
                Discovering Edinburgh's treasures...
              </motion.p>
            </div>
          ) : (
            <motion.div 
              className="attractions-grid"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {filteredAttractions.slice(0, visibleCount).map((attraction, index) => (
                <motion.div
                  key={attraction.id}
                  id={`attraction-${attraction.id}`}
                  className="attraction-card"
                  style={{ minHeight: "425px" }}
                  variants={cardVariants}
                  custom={index}
                  whileHover="hover"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <div className="card-image-wrapper">
                    <img src={attraction.image} alt={attraction.title} />
                    <div className="card-gradient-overlay" />
                    
                    {/* Entrance animation overlay */}
                    <motion.div 
                      className="card-entrance-overlay"
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.05 }}
                    />
                  </div>
                  
                  <div className="card-content">
                    <h3>{attraction.title}</h3>
                    <p>{attraction.description}</p>
                    <div className="card-actions">
                      <Link to={attraction.link || "/info"} className="card-btn">
                        Explore
                        <span className="btn-arrow">→</span>
                      </Link>
                      
                      {/* Add to Calendar Button */}
                      <motion.div 
                        className="add-calendar-btn"
                        whileHover="expanded"
                        initial="collapsed"
                        onClick={() => handleAddToCalendar(attraction)}
                      >
                        <motion.div className="add-calendar-icon">+</motion.div>
                        <motion.span 
                          className="add-calendar-text"
                          variants={{
                            collapsed: { width: 0, opacity: 0 },
                            expanded: { width: "auto", opacity: 1 }
                          }}
                        >
                          Add to Calendar
                        </motion.span>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Load More Button */}
        {hasMore && isLoaded && (
          <ScrollAnimation variants={popIn} className="load-more-container">
            <motion.button 
              className="load-more-btn"
              onClick={loadMore}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              Load More Attractions
            </motion.button>
          </ScrollAnimation>
        )}

        {/* New attractions guide section to add more content */}
        <ScrollAnimation variants={fadeInUp} className="attractions-guide">
          <h2>Visiting Edinburgh Attractions</h2>
          <div className="guide-content">
            <div className="guide-section">
              <h3>Best Time to Visit</h3>
              <p>Edinburgh is beautiful year-round, but each season offers a unique experience. Spring (April-June) and early autumn (September-October) offer pleasant weather and fewer crowds than the peak summer season.</p>
              <p>August is the busiest month due to the Edinburgh Festival Fringe, when the city comes alive with performances and events.</p>
            </div>
            
            <div className="guide-section">
              <h3>Visitor Tips</h3>
              <ul className="guide-tips">
                <li>Many attractions offer discounted tickets if booked online in advance</li>
                <li>Consider an Edinburgh City Pass for multiple attraction visits</li>
                <li>Most major attractions are within walking distance in the city center</li>
                <li>Always check opening times as these may vary seasonally</li>
                <li>Wear comfortable walking shoes - Edinburgh is built on hills!</li>
              </ul>
            </div>
            
            <div className="guide-section">
              <h3>Local Recommendations</h3>
              <p>Take a break between sightseeing at one of Edinburgh's historic pubs or cozy cafés. The city is known for its vibrant food and drink scene.</p>
              <p>Don't miss the panoramic views from Calton Hill - it's less crowded than Arthur's Seat and offers spectacular city vistas with a much easier climb.</p>
            </div>
          </div>
        </ScrollAnimation>
      </section>
    </motion.div>
  );
};

export default ThingsToDo;
